// utils/desktopBridge.js
// Supports both Qt (window.DesktopBridge) and pywebview (window.pywebview.api)

// Detect if running in desktop app
export function isDesktopApp() {
  // Check for Qt bridge user agent
  if (navigator.userAgent.includes("AIEvaluatorDesktop")) {
    return true;
  }
  // Check for pywebview bridge or desktop flag
  if (typeof window !== 'undefined' &&
    (window.__DESKTOP_APP__ || window.pywebview?.api || window.DesktopBridge)) {
    return true;
  }
  return false;
}

// Detect user's platform
export function getPlatform() {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform.toLowerCase();

  if (userAgent.includes('win') || platform.includes('win')) {
    return 'windows';
  } else if (userAgent.includes('mac') || platform.includes('mac')) {
    return 'mac';
  } else if (userAgent.includes('linux') || platform.includes('linux')) {
    return 'linux';
  }

  // Default to platform from navigator
  return 'unknown';
}

// Get platform-specific desktop app download URL from backend
export async function getDesktopAppDownloadUrl() {
  const platform = getPlatform();

  // Linux is not supported
  if (platform === 'linux' || platform === 'unknown') {
    throw new Error(`Desktop app is not available for ${platform}. Please use Windows or Mac.`);
  }

  try {
    const response = await fetch(`/api/get_desktop_app_artifact?platform=${platform}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to get download URL for ${platform}`);
    }

    return {
      url: data.downloadUrl,
      filename: data.filename,
      platform: data.platform
    };
  } catch (error) {
    console.error('[DOWNLOAD] Error fetching download URL:', error);
    throw error;
  }
}

// Legacy constant for backward compatibility (deprecated - use getDesktopAppDownloadUrl instead)
export const DESKTOP_APP_DOWNLOAD_URL = "https://aievaluator.in/desktop-app-download";

// Get the appropriate bridge object
function getBridge() {
  // Prefer pywebview.api (newer WebView2-based app)
  if (window.pywebview?.api) {
    return { type: 'pywebview', api: window.pywebview.api };
  }
  // Fall back to Qt DesktopBridge
  if (window.DesktopBridge) {
    return { type: 'qt', api: window.DesktopBridge };
  }
  return null;
}

// Listen for bridge ready events
let bridgeReadyPromise = null;

function createBridgeReadyPromise() {
  return new Promise((resolve) => {
    // If already available, resolve immediately
    const bridge = getBridge();
    if (bridge) {
      console.log('[BRIDGE] Already available:', bridge.type);
      resolve(true);
      return;
    }

    // Listen for Qt's custom event
    const handler = () => {
      console.log('[BRIDGE] desktopBridgeReady event received');
      window.removeEventListener('desktopBridgeReady', handler);
      resolve(true);
    };

    window.addEventListener('desktopBridgeReady', handler);

    // Fallback: poll for bridge availability with timeout
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds
    const interval = setInterval(() => {
      attempts++;

      const bridge = getBridge();
      if (bridge) {
        clearInterval(interval);
        window.removeEventListener('desktopBridgeReady', handler);
        console.log('[BRIDGE] Found via polling:', bridge.type);
        resolve(true);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        window.removeEventListener('desktopBridgeReady', handler);
        console.error('[BRIDGE] Timeout after ' + (maxAttempts * 100) + 'ms');
        resolve(false);
      }
    }, 100);
  });
}

// Returns Promise<boolean> that resolves when bridge is available
export async function ensureDesktopBridgeReady(timeoutMs = 10000) {
  if (!isDesktopApp()) {
    console.log('[BRIDGE] Not desktop app, skipping');
    return false;
  }

  // Reuse existing promise if already waiting
  if (!bridgeReadyPromise) {
    bridgeReadyPromise = createBridgeReadyPromise();
  }

  try {
    const ready = await Promise.race([
      bridgeReadyPromise,
      new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs))
    ]);

    const bridge = getBridge();
    if (ready && bridge) {
      console.log('[BRIDGE] Ready and verified:', bridge.type);
      return true;
    } else {
      console.warn('[BRIDGE] Not ready or not available');
      return false;
    }
  } catch (error) {
    console.error('[BRIDGE] Error:', error);
    return false;
  }
}

// Start proctoring with test info
export async function startDesktopProctoring(testId, testType) {
  if (!isDesktopApp()) {
    console.log('[BRIDGE] Not desktop app, proctoring skipped');
    return { success: false, error: 'Not in desktop app' };
  }

  const ready = await ensureDesktopBridgeReady();
  if (!ready) {
    console.error('[BRIDGE] Bridge not available, cannot start proctoring');
    return { success: false, error: 'Bridge not available' };
  }

  const bridge = getBridge();
  if (!bridge) {
    console.error('[BRIDGE] No bridge found');
    return { success: false, error: 'No bridge found' };
  }

  try {
    console.log('[BRIDGE] Starting proctoring via', bridge.type, '...');

    let result;
    if (bridge.type === 'pywebview') {
      // pywebview.api methods return promises
      result = await bridge.api.start_proctoring(testId, testType);
    } else {
      // Qt bridge uses sync methods
      bridge.api.startProctoring(testId, testType);
      result = { success: true };
    }

    console.log('[BRIDGE] Proctoring started:', result);
    return result || { success: true };
  } catch (error) {
    console.error('[BRIDGE] Error starting proctoring:', error);
    return { success: false, error: error.message };
  }
}

// Stop proctoring and get summary
export async function stopDesktopProctoring() {
  if (!isDesktopApp()) {
    return { success: false, error: 'Not in desktop app' };
  }

  const bridge = getBridge();
  if (!bridge) {
    console.warn('[BRIDGE] No bridge found for stop');
    return { success: false, error: 'No bridge found' };
  }

  try {
    console.log('[BRIDGE] Stopping proctoring via', bridge.type, '...');

    let result;
    if (bridge.type === 'pywebview') {
      result = await bridge.api.stop_proctoring();
    } else {
      bridge.api.stopProctoring();
      result = { success: true };
    }

    console.log('[BRIDGE] Proctoring stopped:', result);
    return result || { success: true };
  } catch (error) {
    console.error('[BRIDGE] Error stopping proctoring:', error);
    return { success: false, error: error.message };
  }
}

// Log a cheating event
export async function logCheatingEvent(eventType, severity, details) {
  if (!isDesktopApp()) {
    return null;
  }

  const bridge = getBridge();
  if (!bridge) {
    return null;
  }

  try {
    if (bridge.type === 'pywebview') {
      return await bridge.api.log_cheating_event(eventType, severity, details);
    } else if (bridge.api.logCheatingEvent) {
      bridge.api.logCheatingEvent(eventType, severity, details);
      return { success: true };
    }
  } catch (error) {
    console.error('[BRIDGE] Error logging cheating event:', error);
  }
  return null;
}

// Get activity summary
export async function getActivitySummary() {
  if (!isDesktopApp()) {
    return null;
  }

  const bridge = getBridge();
  if (!bridge) {
    return null;
  }

  try {
    if (bridge.type === 'pywebview') {
      return await bridge.api.get_activity_summary();
    } else if (bridge.api.getActivitySummary) {
      return bridge.api.getActivitySummary();
    }
  } catch (error) {
    console.error('[BRIDGE] Error getting activity summary:', error);
  }
  return null;
}

// Get cheating events
export async function getCheatingEvents() {
  if (!isDesktopApp()) {
    return [];
  }

  const bridge = getBridge();
  if (!bridge) {
    return [];
  }

  try {
    if (bridge.type === 'pywebview') {
      return await bridge.api.get_cheating_events();
    } else if (bridge.api.getCheatingEvents) {
      return bridge.api.getCheatingEvents();
    }
  } catch (error) {
    console.error('[BRIDGE] Error getting cheating events:', error);
  }
  return [];
}

// Get network monitoring data (ETW)
export async function getNetworkData() {
  if (!isDesktopApp()) {
    return [];
  }

  const bridge = getBridge();
  if (!bridge) {
    return [];
  }

  try {
    if (bridge.type === 'pywebview') {
      return await bridge.api.get_network_data();
    } else if (bridge.api.getNetworkData) {
      return bridge.api.getNetworkData();
    }
  } catch (error) {
    console.error('[BRIDGE] Error getting network data:', error);
  }
  return [];
}

// Get real-time cheating violations
export async function getViolations() {
  if (!isDesktopApp()) {
    return [];
  }

  const bridge = getBridge();
  if (!bridge) {
    return [];
  }

  try {
    if (bridge.type === 'pywebview') {
      return await bridge.api.get_violations();
    } else if (bridge.api.getViolations) {
      return bridge.api.getViolations();
    }
  } catch (error) {
    console.error('[BRIDGE] Error getting violations:', error);
  }
  return [];
}

// Log clipboard event (paste, copy, cut) for server-side tracking
export async function logClipboardEvent(eventType, contentLength, preview = null) {
  if (!isDesktopApp()) {
    return null;
  }

  const bridge = getBridge();
  if (!bridge) {
    return null;
  }

  try {
    if (bridge.type === 'pywebview') {
      return await bridge.api.log_clipboard_event(eventType, contentLength, preview);
    } else if (bridge.api.logClipboardEvent) {
      bridge.api.logClipboardEvent(eventType, contentLength, preview);
      return { logged: true };
    }
  } catch (error) {
    console.error('[BRIDGE] Error logging clipboard event:', error);
  }
  return null;
}

// Submit proctoring data to backend
export async function submitProctoringData(testId, testType) {
  if (!isDesktopApp()) {
    return { success: false, error: 'Not in desktop app' };
  }

  try {
    const summary = await getActivitySummary();
    const cheatingEvents = await getCheatingEvents();
    const networkData = await getNetworkData();

    console.log('[BRIDGE] Submitting proctoring data:', {
      test_id: testId,
      activity_entries: summary ? Object.keys(summary).length : 0,
      cheating_events: cheatingEvents.length,
      network_entries: networkData.length
    });

    const response = await fetch('/api/submit_proctoring_data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        test_id: testId,
        test_type: testType,
        activity_summary: summary,
        cheating_events: cheatingEvents,
        network_data: networkData,
        timestamp: new Date().toISOString()
      })
    });

    return await response.json();
  } catch (error) {
    console.error('[BRIDGE] Error submitting proctoring data:', error);
    return { success: false, error: error.message };
  }
}

// Get recording info from Python bridge
export async function getRecordingInfo() {
  if (!isDesktopApp()) {
    return null;
  }

  const bridge = getBridge();
  if (!bridge || bridge.type !== 'pywebview') {
    return null;
  }

  try {
    return await bridge.api.get_recording_info();
  } catch (error) {
    console.error('[BRIDGE] Error getting recording info:', error);
    return null;
  }
}

// Upload screen recording to backend
export async function uploadRecording(testId) {
  if (!isDesktopApp()) {
    return { success: false, error: 'Not in desktop app' };
  }

  const bridge = getBridge();
  if (!bridge) {
    return { success: false, error: 'No bridge found' };
  }

  try {
    if (bridge.type === 'pywebview') {
      // Use Python-side upload to bypass WebKit mixed content blocking
      // (WebKit blocks HTTP localhost requests from HTTPS pages)
      console.log('[BRIDGE] Uploading recording via Python bridge...');
      const result = await bridge.api.upload_recording();
      console.log('[BRIDGE] Python upload result:', result);
      return result;
    }

    // Fallback for Qt bridge: use localhost fetch (works in Qt WebEngine)
    const recordingInfo = await getRecordingInfo();
    if (!recordingInfo) {
      console.warn('[BRIDGE] No recording info available');
      return { success: false, error: 'No recording available' };
    }

    console.log('[BRIDGE] Fetching recording from:', recordingInfo.localUrl);

    const videoResponse = await fetch(recordingInfo.localUrl);
    if (!videoResponse.ok) {
      throw new Error(`Failed to fetch recording: ${videoResponse.status}`);
    }

    const videoBlob = await videoResponse.blob();
    console.log('[BRIDGE] Recording fetched, size:', videoBlob.size);

    const formData = new FormData();
    formData.append('video', videoBlob, recordingInfo.filename);
    formData.append('test_id', testId || recordingInfo.testId);

    const uploadResponse = await fetch('/api/upload_full_screen_video', {
      method: 'POST',
      body: formData
    });

    const result = await uploadResponse.json();
    console.log('[BRIDGE] Recording uploaded:', result);

    return { success: true, ...result };
  } catch (error) {
    console.error('[BRIDGE] Error uploading recording:', error);
    return { success: false, error: error.message };
  }
}